import React, {
    InputHTMLAttributes,
    ReactElement,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes,
    cloneElement,
    forwardRef,
    useId
} from 'react';
import classNames from 'classnames';
import {ChevronDownIcon, CircleAlertIcon, UploadIcon, XIcon} from 'lucide-react';
import Icon from '../shared/Icon.tsx';

interface FormFieldProps {
    id?: string;
    label: ReactNode;
    // Mark the optional fields, not the required ones.
    optional?: boolean;
    // Helper text below; an error replaces it in the same place.
    helper?: ReactNode;
    error?: ReactNode;
    // A counter or link on the right of the helper line.
    aside?: ReactNode;
    // The single control. It receives id, aria-describedby and aria-invalid.
    children: ReactElement;
    className?: string;
}

/*
 * Label above (no colon), the control, then helper or error text. Fields are
 * white with a visible outline, 16px text, and a focus ring that's always
 * on (from the Bootstrap variables).
 */
export const FormField: React.FC<FormFieldProps> = ({
    id,
    label,
    optional,
    helper,
    error,
    aside,
    children,
    className
}) => {
    const generated = useId();
    const fieldId = id ?? generated;
    const messageId = `${fieldId}-message`;
    const hasMessage = !!(helper || error);

    const control = cloneElement(children, {
        id: fieldId,
        'aria-describedby': hasMessage ? messageId : undefined,
        'aria-invalid': error ? true : undefined
    });

    return (
        <div className={classNames('mx-field', {'mx-field-invalid': !!error}, className)}>
            <label htmlFor={fieldId} className="mx-field-label">
                {label}
                {optional && <span className="mx-field-optional"> (optional)</span>}
            </label>
            {control}
            {(hasMessage || aside) && (
                <div className="mx-field-message-row">
                    {error ? (
                        <span id={messageId} className="mx-field-error">
                            <span className="mx-field-error-marker">
                                <Icon icon={CircleAlertIcon} size={10} strokeWidth={3.5} />
                            </span>
                            {error}
                        </span>
                    ) : (
                        helper && (
                            <span id={messageId} className="mx-field-helper">
                                {helper}
                            </span>
                        )
                    )}
                    {aside && <span className="mx-field-aside">{aside}</span>}
                </div>
            )}
        </div>
    );
};

type TextFieldProps = InputHTMLAttributes<HTMLInputElement> & {
    // Text fixed to the start of the field, e.g. "@" before a handle.
    prefix?: ReactNode;
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
    ({prefix, className, ...props}, ref) =>
        prefix ? (
            <span className={classNames('mx-input-group', className)}>
                <span className="mx-input-prefix">{prefix}</span>
                <input ref={ref} className="form-control" {...props} />
            </span>
        ) : (
            <input
                ref={ref}
                className={classNames('form-control', className)}
                {...props}
            />
        )
);
TextField.displayName = 'TextField';

export const TextArea = forwardRef<
    HTMLTextAreaElement,
    TextareaHTMLAttributes<HTMLTextAreaElement>
>(({className, rows = 3, ...props}, ref) => (
    <textarea
        ref={ref}
        rows={rows}
        className={classNames('form-control', className)}
        {...props}
    />
));
TextArea.displayName = 'TextArea';

export const SelectField = forwardRef<
    HTMLSelectElement,
    SelectHTMLAttributes<HTMLSelectElement>
>(({className, children, ...props}, ref) => (
    <span className="mx-select">
        <select ref={ref} className={classNames('form-select', className)} {...props}>
            {children}
        </select>
        <Icon icon={ChevronDownIcon} size="s" className="mx-select-chevron" />
    </span>
));
SelectField.displayName = 'SelectField';

interface CheckProps extends InputHTMLAttributes<HTMLInputElement> {
    label: ReactNode;
    // A second line under the label.
    description?: ReactNode;
}

// For forms that are saved with a button.
export const Checkbox: React.FC<CheckProps> = ({
    label,
    description,
    className,
    id,
    ...props
}) => {
    const generated = useId();
    const inputId = id ?? generated;
    return (
        <div className={classNames('form-check mx-check', className)}>
            <input type="checkbox" className="form-check-input" id={inputId} {...props} />
            <label htmlFor={inputId} className="form-check-label">
                {label}
                {description && <span className="mx-check-description">{description}</span>}
            </label>
        </div>
    );
};

// For a choice of two to five options that should all be visible.
export const Radio: React.FC<CheckProps> = ({
    label,
    description,
    className,
    id,
    ...props
}) => {
    const generated = useId();
    const inputId = id ?? generated;
    return (
        <div className={classNames('form-check mx-check', className)}>
            <input type="radio" className="form-check-input" id={inputId} {...props} />
            <label htmlFor={inputId} className="form-check-label">
                {label}
                {description && <span className="mx-check-description">{description}</span>}
            </label>
        </div>
    );
};

interface RadioCardProps extends CheckProps {
    // Controls that appear inside the card once it is selected.
    children?: ReactNode;
}

// A radio drawn as a card, for choices with a sentence of explanation each.
// The selected card gets a purple outline; the rest stay neutral.
export const RadioCard: React.FC<RadioCardProps> = ({
    label,
    description,
    children,
    className,
    id,
    checked,
    ...props
}) => {
    const generated = useId();
    const inputId = id ?? generated;
    return (
        <label
            htmlFor={inputId}
            className={classNames('mx-radio-card', {active: checked}, className)}
        >
            <input
                type="radio"
                className="form-check-input"
                id={inputId}
                checked={checked}
                {...props}
            />
            <span className="mx-radio-card-text">
                <b>{label}</b>
                {description && <span>{description}</span>}
                {checked && children && (
                    <span className="mx-radio-card-extra">{children}</span>
                )}
            </span>
        </label>
    );
};

// Only for settings that take effect immediately, with no Save.
export const Toggle: React.FC<CheckProps> = ({label, className, id, ...props}) => {
    const generated = useId();
    const inputId = id ?? generated;
    return (
        <div className={classNames('form-check form-switch mx-toggle', className)}>
            <input
                type="checkbox"
                role="switch"
                className="form-check-input"
                id={inputId}
                {...props}
            />
            <label htmlFor={inputId} className="form-check-label">
                {label}
            </label>
        </div>
    );
};

interface FileDropZoneProps {
    // "Drop images, audio, video or 3D models"
    title: ReactNode;
    // "PNG, JPG, MP3 · up to 50 MB each"
    detail?: ReactNode;
    buttonLabel?: string;
    accept?: string;
    multiple?: boolean;
    // Click the zone, then paste (Ctrl+V / ⌘V) to hand over files from the clipboard.
    pasteable?: boolean;
    onFiles: (files: File[]) => void;
    className?: string;
}

// Replaces the native "Choose file / No file chosen" control.
export const FileDropZone: React.FC<FileDropZoneProps> = ({
    title,
    detail,
    buttonLabel = 'Choose files',
    accept,
    multiple,
    pasteable,
    onFiles,
    className
}) => {
    const inputId = useId();
    const [over, setOver] = React.useState(false);

    return (
        <div
            className={classNames('mx-dropzone', {'mx-dropzone-over': over}, className)}
            tabIndex={pasteable ? 0 : undefined}
            onPaste={
                pasteable
                    ? e => {
                          const files = Array.from(e.clipboardData.files);
                          if (files.length === 0) return;
                          e.preventDefault();
                          onFiles(files);
                      }
                    : undefined
            }
            onDragOver={e => {
                e.preventDefault();
                setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={e => {
                e.preventDefault();
                setOver(false);
                onFiles(Array.from(e.dataTransfer.files));
            }}
        >
            <span className="mx-dropzone-icon">
                <Icon icon={UploadIcon} size="s" />
            </span>
            <span className="mx-dropzone-text">
                <b>{title}</b>
                {detail && <span>{detail}</span>}
            </span>
            <label htmlFor={inputId} className="btn btn-secondary mb-0">
                {buttonLabel}
            </label>
            <input
                id={inputId}
                type="file"
                className="visually-hidden"
                accept={accept}
                multiple={multiple}
                onChange={e => {
                    onFiles(Array.from(e.target.files ?? []));
                    e.target.value = '';
                }}
            />
        </div>
    );
};

interface TagsFieldProps {
    id?: string;
    tags: string[];
    onChange: (tags: string[]) => void;
    max?: number;
    placeholder?: string;
    'aria-describedby'?: string;
    'aria-invalid'?: boolean;
}

// Tags as neutral chips with a remove button, and a field to add more.
export const TagsField: React.FC<TagsFieldProps> = ({
    id,
    tags,
    onChange,
    max,
    placeholder = 'Add a tag',
    ...aria
}) => {
    const [draft, setDraft] = React.useState('');
    const full = max !== undefined && tags.length >= max;

    const add = () => {
        const value = draft.trim();
        if (value && !tags.includes(value) && !full) {
            onChange([...tags, value]);
        }
        setDraft('');
    };

    return (
        <div className="mx-tags-field">
            {tags.map(tag => (
                <span key={tag} className="mx-tag mx-tag-removable">
                    {tag}
                    <button
                        type="button"
                        aria-label={`Remove ${tag}`}
                        onClick={() => onChange(tags.filter(t => t !== tag))}
                    >
                        <Icon icon={XIcon} size={12} strokeWidth={2.5} />
                    </button>
                </span>
            ))}
            <input
                id={id}
                type="text"
                className="mx-tags-input"
                placeholder={full ? '' : placeholder}
                disabled={full}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        add();
                    } else if (e.key === 'Backspace' && !draft && tags.length) {
                        onChange(tags.slice(0, -1));
                    }
                }}
                onBlur={add}
                {...aria}
            />
        </div>
    );
};
